import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Tag, Plus, Pencil, Trash2, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CategoriesManagement({ user }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');

  useEffect(() => { fetchCategories(); }, []);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/categories`, { headers: { Authorization: `Bearer ${token}` } });
      setCategories(res.data);
    } catch { toast.error('Failed to load categories'); }
    finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/categories`, { name: newName.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Category created');
      setNewName('');
      fetchCategories();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create'); }
  };

  const handleUpdate = async (id) => {
    if (!editName.trim()) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/categories/${id}`, { name: editName.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Category updated');
      setEditId(null);
      fetchCategories();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? Credentials using it will be moved to "Other".`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/categories/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Category deleted');
      fetchCategories();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const isAdmin = user?.role === 'admin';

  return (
    <div className="p-8">
      <div className="max-w-2xl">
        <div className="flex items-center gap-3 mb-2">
          <Tag className="w-8 h-8 text-primary" />
          <h1 className="font-manrope font-bold text-4xl text-text-primary tracking-tight">Categories</h1>
        </div>
        <p className="text-text-secondary mb-8">Organize your credentials with custom categories</p>

        {/* Add Category (Admin only) */}
        {isAdmin && (
          <form onSubmit={handleCreate} className="flex gap-3 mb-8" data-testid="add-category-form">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="New category name..."
              className="flex-1 bg-white border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-primary focus:outline-none text-text-primary"
              data-testid="input-new-category"
            />
            <button type="submit" className="btn-primary px-6" data-testid="btn-add-category">
              <Plus size={18} className="mr-1 inline" /> Add
            </button>
          </form>
        )}

        {/* Categories List */}
        <div className="space-y-2">
          {categories.map((cat) => (
            <div
              key={cat.id}
              className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-between hover:border-gray-300 transition-colors"
              data-testid={`category-${cat.id}`}
            >
              {editId === cat.id ? (
                <div className="flex items-center gap-2 flex-1">
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="flex-1 bg-white border-2 border-primary rounded-lg px-3 py-2 focus:outline-none text-text-primary text-sm"
                    autoFocus
                    data-testid="input-edit-category"
                  />
                  <button onClick={() => handleUpdate(cat.id)} className="btn-primary px-3 py-2 text-sm" data-testid="btn-save-category">Save</button>
                  <button onClick={() => setEditId(null)} className="btn-outline px-3 py-2 text-sm">Cancel</button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-primary/30" />
                    <span className="font-medium text-text-primary">{cat.name}</span>
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => { setEditId(cat.id); setEditName(cat.name); }}
                        className="p-2 text-text-muted hover:text-primary transition-colors"
                        data-testid={`btn-edit-cat-${cat.id}`}
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(cat.id, cat.name)}
                        className="p-2 text-text-muted hover:text-accent transition-colors"
                        data-testid={`btn-delete-cat-${cat.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
          {!loading && categories.length === 0 && (
            <p className="text-text-muted text-center py-8">No categories yet</p>
          )}
        </div>
      </div>
    </div>
  );
}
